import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Card, Form, Input, Button, Typography, Result, Alert } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { PublicLayout } from "../../shared/components/layout";
import { useAntdTheme } from "../../shared/hooks";
import { acceptInvite } from "./api";

const { Title, Text } = Typography;

export const AcceptInvitePage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { tokenId } = useParams<{ tokenId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const secret = searchParams.get("s");

  useEffect(() => {
    if (!tokenId || !secret) {
      setError(t("invalid_invite_link"));
    }
  }, [tokenId, secret, t]);

  const handleSubmit = async () => {
    if (!tokenId || !secret) return;

    try {
      const values = await form.validateFields();
      setLoading(true);
      setError(null);

      acceptInvite(
        {
          token_id: tokenId,
          secret: secret,
          new_password: values.password,
        },
        () => {
          setLoading(false);
          setSuccess(true);
        },
        (err) => {
          setLoading(false);
          if (err instanceof Error) {
            setError(err.message);
          } else {
            setError(t("failed_to_accept_invite"));
          }
        }
      );
    } catch {
      // Form validation failed
    }
  };

  const handleGoToLogin = () => {
    navigate("/login");
  };

  if (success) {
    return (
      <PublicLayout>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            background: token.colorPrimary,
            padding: token.paddingLG,
          }}
        >
          <Card
            style={{
              width: "100%",
              maxWidth: 450,
              borderRadius: token.borderRadiusLG,
              boxShadow: token.boxShadow,
            }}
            styles={{
              body: {
                padding: token.paddingXL,
              },
            }}
          >
            <Result
              status="success"
              title={t("password_set_successfully")}
              subTitle={t("you_can_now_login")}
              extra={
                <Button
                  type="primary"
                  onClick={handleGoToLogin}
                  style={{
                    height: token.controlHeightLG,
                    borderRadius: token.borderRadiusLG,
                    fontSize: token.fontSizeLG,
                  }}
                >
                  {t("go_to_login")}
                </Button>
              }
            />
          </Card>
        </div>
      </PublicLayout>
    );
  }

  if (error && (!tokenId || !secret)) {
    return (
      <PublicLayout>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            background: token.colorPrimary,
            padding: token.paddingLG,
          }}
        >
          <Card
            style={{
              width: "100%",
              maxWidth: 450,
              borderRadius: token.borderRadiusLG,
              boxShadow: token.boxShadow,
            }}
            styles={{
              body: {
                padding: token.paddingXL,
              },
            }}
          >
            <Result
              status="error"
              title={t("invalid_invite_link")}
              subTitle={t("invalid_invite_link_description")}
              extra={
                <Button
                  type="primary"
                  onClick={handleGoToLogin}
                  style={{
                    height: token.controlHeightLG,
                    borderRadius: token.borderRadiusLG,
                    fontSize: token.fontSizeLG,
                  }}
                >
                  {t("go_to_login")}
                </Button>
              }
            />
          </Card>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: token.colorPrimary,
          padding: token.paddingLG,
        }}
      >
        <Card
          style={{
            width: "100%",
            maxWidth: 450,
            borderRadius: token.borderRadiusLG,
            boxShadow: token.boxShadow,
          }}
          styles={{
            body: {
              padding: token.paddingXL,
            },
          }}
        >
          <div style={{ textAlign: "center", marginBottom: token.paddingXL }}>
            <LockOutlined
              style={{
                fontSize: 48,
                color: token.colorPrimary,
                marginBottom: token.marginMD,
              }}
            />
            <Title
              level={3}
              style={{
                color: token.colorPrimary,
                marginBottom: token.paddingXS,
              }}
            >
              {t("set_your_password")}
            </Title>
            <Text type="secondary" style={{ fontSize: token.fontSize }}>
              {t("set_password_description")}
            </Text>
          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              style={{
                marginBottom: token.marginLG,
                borderRadius: token.borderRadiusLG,
              }}
            />
          )}

          <Form form={form} layout="vertical" size="large" onFinish={handleSubmit}>
            <Form.Item
              name="password"
              label={t("new_password")}
              rules={[
                { required: true, message: t("please_enter_password") },
                { min: 6, message: t("password_min_length") },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: token.colorTextTertiary }} />}
                placeholder={t("enter_new_password")}
                style={{
                  borderRadius: token.borderRadiusLG,
                  height: token.controlHeightLG,
                }}
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label={t("confirm_password")}
              dependencies={["password"]}
              rules={[
                { required: true, message: t("please_confirm_password") },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t("passwords_do_not_match")));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: token.colorTextTertiary }} />}
                placeholder={t("confirm_your_password")}
                style={{
                  borderRadius: token.borderRadiusLG,
                  height: token.controlHeightLG,
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  width: "100%",
                  height: token.controlHeightLG,
                  borderRadius: token.borderRadiusLG,
                  fontSize: token.fontSizeLG,
                  fontWeight: token.fontWeightStrong,
                  marginTop: token.marginMD,
                }}
              >
                {t("set_password")}
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <div style={{ textAlign: "center", marginTop: token.marginXL }}>
          <Text
            style={{
              color: token.colorTextLightSolid,
              fontSize: token.fontSizeSM,
            }}
          >
            © {new Date().getFullYear()} TicketRails. All rights reserved.
          </Text>
        </div>
      </div>
    </PublicLayout>
  );
};
